import { useRef, useCallback, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import useCommonToast from '../../hooks/useCommonToast';
import {
    getBasisScalar,
    getValidScalarRows,
    modifyScalar,
    deleteAllScalarData,
    columnSchemaOnScalarGrid,
    validateParsedNewRowsOnScalarGrid,
    addScalarRows,
    hasAppliedBasisFilters,
} from '../../services/basis';
import { Flex, Box, Spacer, useDisclosure } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';

import SecondaryIconButton from './../utils/SecondaryIconButton';
import SideDrawer from './../utils/SideDrawer';
import BasisFilters from './../utils/BasisFilters';

import { lenientParse, isNumberCellEmpty } from '../../services/items';
import useTabularView from '../../hooks/useTabularView';

import { ReactComponent as FilterIcon } from '../../icons/filter.svg';

import DeleteAllButton from '../itemData/DeleteAllButton';
import AddRowsButton from '../itemData/AddRowsButton';

import DataGridWrapper from '../grid/DataGridWrapper';
import DataGrid from '../grid/DataGrid';

const ScalarTab = ({ options, basis, filters, onFiltersChange, item, onSortingChange, newDataImported }) => {
    const dataTabularView = useTabularView('nope_i_dont_care_about_this', NaN);

    const gridApi = useRef(null);
    const toast = useCommonToast();
    const intl = useIntl();

    const filterDrawer = useDisclosure();

    const basisLabel = intl.formatMessage({ id: 'basis_title' });

    useEffect(() => {
        if (gridApi.current) {
            gridApi.current.refreshServerSideStore({
                purge: true,
            });
        }
    }, [newDataImported]);

    const onGridReady = (params) => {
        gridApi.current = params.api;
    };

    const getServerSideData = useCallback(
        async (params) => {
            const page = params.request.startRow / 500 + 1;
            const sortColumn = params.request.sortModel[0] || null;
            const orderBy = sortColumn ? sortColumn.colId : null;
            const orderByDescending = sortColumn ? sortColumn.sort === 'desc' : true;

            const payload = {
                startDateFrom: filters.startDateFrom || '2010-02-25T15:31:30.555Z',
                startDateTo: filters.startDateTo || '2022-02-25T15:31:30.555Z',
                paging: {
                    page,
                    pageSize: 500,
                },
                sorting:
                    orderBy === null
                        ? []
                        : [
                              {
                                  orderBy,
                                  orderByDescending,
                              },
                          ],
            };

            const data = await getBasisScalar(basis.basisId, payload);

            onSortingChange({
                sortColumn: orderBy,
                sortByDescending: orderByDescending,
            });

            if (!data.totalCount) {
                params.api.showNoRowsOverlay();
            } else {
                params.api.hideOverlay();
            }

            params.success({
                rowData: data.items,
                rowCount: data.totalCount,
            });
        },
        [basis.basisId, filters.startDateFrom, filters.startDateTo, onSortingChange]
    );

    const gridColumns = [
        {
            field: 'startDate',
            type: 'date',
            headerName: intl.formatMessage({ id: 'basis_tabs_start_date' }),
            resizable: true,
            sortable: true,
        },
        {
            field: 'endDate',
            type: 'date',
            headerName: intl.formatMessage({ id: 'basis_tabs_end_date' }),
            resizable: true,
            sortable: true,
        },
        {
            type: 'checkbox',
            field: 'fullDestination',
            headerName: intl.formatMessage({ id: 'basis_tabs_full_destination' }),
            resizable: true,
        },
        {
            field: 'minValue',
            type: 'number',
            headerName: intl.formatMessage({ id: 'basis_tabs_min_value' }),
            resizable: true,
            sortable: true,
        },
        {
            field: 'maxValue',
            type: 'number',
            headerName: intl.formatMessage({ id: 'basis_tabs_max_value' }),
            resizable: true,
            sortable: true,
        },
        {
            field: 'meanValue',
            type: 'number',
            headerName: intl.formatMessage({ id: 'basis_tabs_mean_value' }),
            resizable: true,
            sortable: true,
        },
        {
            field: 'stdDev',
            type: 'number',
            headerName: intl.formatMessage({ id: 'basis_tabs_std_dev' }),
            resizable: true,
            sortable: true,
        },
        {
            field: 'peakPeriodId',
            type: 'select',
            cellEditorParams: { options: options.peakPeriods, required: true },
            headerName: intl.formatMessage({ id: 'basis_tab_peak_periods' }),
            resizable: true,
            minWidth: 180,
        },
        {
            field: 'statUnitId',
            type: 'select',
            cellEditorParams: { options: options.statUnits, required: true },
            headerName: intl.formatMessage({ id: 'basis_tabs_stat_unit' }),
            resizable: true,
            minWidth: 180,
        },
    ];

    const addCols = gridColumns.map((c) => ({
        field: c.field,
        headerName: c.headerName,
    }));

    const updateScalarData = async (rowIds) => {
        const rows = rowIds.map((id) => gridApi.current.getRowNode(id));

        const validRows = getValidScalarRows(rows);

        if (validRows.length === 0) return;

        try {
            await modifyScalar(validRows);

            toast(intl.formatMessage({ id: 'basis_save_changes_success' }));
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: intl.formatMessage({ id: 'common_generic_saving_error' }),
                });
            }
        }
    };

    const formatRows = (parsedData) => {
        return parsedData.map((row) => {
            return {
                ...row,

                fullDestination: Number(row.fullDestination) === 1 ? true : false,

                startDate: lenientParse(row.startDate).toISOString(),
                endDate: lenientParse(row.endDate).toISOString(),

                peakPeriodId: options.peakPeriods.find(({ description }) => description === row.peakPeriodId)?.id,
                statUnitId: options.statUnits.find(({ description }) => description === row.statUnitId)?.id,

                minValue: isNumberCellEmpty(row.minValue),
                maxValue: isNumberCellEmpty(row.maxValue),
                meanValue: isNumberCellEmpty(row.meanValue),
                stdDev: isNumberCellEmpty(row.stdDev),
            };
        });
    };

    const onAddRows = async (newRows) => {
        const { setIsLoading, addRows } = dataTabularView;

        const successMessage = intl.formatMessage({ id: 'common_generic_item_change_success' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            setIsLoading(true);

            await addScalarRows(basis.basisId, newRows);

            gridApi.current.refreshServerSideStore({
                purge: true,
            });

            setIsLoading(false);
            addRows.onClose();

            toast(successMessage);
        } catch (error) {
            setIsLoading(false);

            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    const onApplyFilters = (newFilters) => {
        filterDrawer.onClose();

        onFiltersChange(newFilters);
    };

    return (
        <>
            <Box>
                <Flex mb={6} direction={{ base: 'column', xl: 'row' }}>
                    <Spacer />
                    <SecondaryIconButton
                        leftIcon={<CustomFilterIcon />}
                        rightIcon={hasAppliedBasisFilters(filters) && <CustomFilterIndicatorIcon />}
                        type="button"
                        variant="secondary"
                        onClick={filterDrawer.onOpen}
                        size="sm"
                        mb={{ base: 4, xl: 0 }}
                        mr={{ base: 0, xl: 4 }}
                    >
                        <FormattedMessage id="common_filters_btn" />
                    </SecondaryIconButton>

                    <DeleteAllButton
                        subItemId={basis.basisId}
                        deleteCallback={deleteAllScalarData}
                        successCallback={() => {}}
                        refetchData={() => {
                            gridApi.current.refreshServerSideStore({
                                purge: true,
                            });
                        }}
                        isInTabularTab={true}
                    />

                    <AddRowsButton
                        modal={dataTabularView.addRows}
                        isLoading={dataTabularView.isLoading}
                        disabled={false}
                        tabIndex={0}
                        validate={validateParsedNewRowsOnScalarGrid}
                        format={formatRows}
                        columns={addCols}
                        columnSchema={{
                            ...columnSchemaOnScalarGrid,
                            peakPeriodId: {
                                ...columnSchemaOnScalarGrid.peakPeriodId,
                                constraints: options.peakPeriods.map((elem) => elem.description),
                            },
                            unitStatId: {
                                ...columnSchemaOnScalarGrid.unitStatId,
                                constraints: options.statUnits.map((elem) => elem.description),
                            },
                        }}
                        addRows={onAddRows}
                    />
                </Flex>
                <DataGridWrapper>
                    <DataGrid
                        isInitialRequestSent={true}
                        onGridReady={onGridReady}
                        enableBrowserTooltips={true}
                        serverSideDatasource={{ getRows: getServerSideData }}
                        onDataChange={updateScalarData}
                        noDataMessageId="basis_no_data"
                        paginationPageSize={500}
                        cacheBlockSize={500}
                        rowModelType="serverSide"
                        serverSideStoreType="partial"
                        columns={gridColumns}
                    />
                </DataGridWrapper>
            </Box>

            {filterDrawer.isOpen && (
                <SideDrawer
                    isOpen
                    onClose={filterDrawer.onClose}
                    header={<FormattedMessage id="common_filter_heading" values={{ item: basisLabel }} />}
                    content={<BasisFilters onApplyFilters={onApplyFilters} item={item} />}
                    placement="right"
                />
            )}
        </>
    );
};

const CustomFilterIcon = styled(FilterIcon)`
    path {
        fill: #677380;
    }
`;

const CustomFilterIndicatorIcon = styled.div`
    background: var(--chakra-colors-blue-400);
    height: 6px;
    width: 6px;
    border-radius: var(--chakra-radii-md);
    margin-top: -10px;
`;

export default ScalarTab;
